<template>
  <div class="pt-4 flex flex-col gap-y-1">
    <div class="flex justify-between border-b border-brand-gray-300">
      <h2 class="text-xl md:text-2xl text-primary font-medium">
        {{ $t('smartservice') }}
      </h2>
      <div class="flex gap-x-3">
        {{ value }}
        <button @click="selectedView = 'list'">
          <faGripLines
            :class="[selectedView == 'list' ? 'fill-primary' : 'fill-brand-black-secondary']"
            class="w-5"
          />
        </button>
        <button @click="selectedView = 'grid'">
          <faGrip
            :class="[selectedView == 'grid' ? 'fill-primary' : 'fill-brand-black-secondary']"
            class="w-5"
          />
        </button>
      </div>
    </div>

    <DashboardServiceDrillDown
      v-if="!isLoading"
      :selection-filter="dashboardStore.selectionFilter"
      :regions="sponsorDepartments"
      :modules="dashboardStore.modules"
      :services="dashboardStore.services"
      :sub-services="dashboardStore.subServices"
      @on-region-change="dashboardStore.handleRegionChange"
      @on-module-change="dashboardStore.handleModuleChange"
      @on-service-change="dashboardStore.handleServiceChange"
      @on-subservice-change="dashboardStore.handleSubserviceChange"
    ></DashboardServiceDrillDown>
    <ServiceDirectory
      :key="isLoading"
      :services="dashboardStore.directoryItems"
      :view-as="selectedView"
      v-if="!isLoading && dashboardStore.directoryItems.length > 0"
    >
      <template #gridCardCorner="{ service }">
        <div class="flex gap-x-3">
          <FavoriteButton
            :is-favorited="favoritesStore.isInFavorite(service)"
            @on-click="favoritesStore.toggleFavoriteStatus(service)"
          />
        </div>
      </template>
      <template #gridCardActionsS="{ service }">
        <button
          class="w-fit ps-1 rounded-md bg-primary/80 hover:bg-primary flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed font-normal"
          @click="onStartService(service)"
        >
          <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
            {{ $t('serviceStart') }}
          </span>
          <div class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-e">
            <FontAwesomeIcon
              :icon="faArrowCircleRight"
              class="w-4 fill-primary/80 hover:fill-primary text-white"
            />
          </div>
        </button>
      </template>
      <template #gridCardActionsE="{ service }">
        <RouterLink
          :to="{ name: 'ServiceCardView', params: { serviceId: service.transactionId } }"
          target="_blank"
          class="w-fit pe-1 rounded-md bg-blue-500/80 hover:bg-blue-500 flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed font-normal"
        >
          <div class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-s">
            <faListAlt class="w-4 fill-white" />
          </div>
          <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
            {{ $t('serviceCard') }}
          </span>
        </RouterLink>
      </template>

      <template #rowActions="{ service }">
        <FavoriteButton
          :is-favorited="favoritesStore.isInFavorite(service)"
          @on-click="favoritesStore.toggleFavoriteStatus(service)"
        />
        <RouterLink
          :to="{ name: 'ServiceCardView', params: { serviceId: service.transactionId } }"
          target="_blank"
          class="w-fit pe-1 rounded-md bg-blue-500/80 hover:bg-blue-500 flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-start"
        >
          <div class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-s">
            <faListAlt class="w-4 fill-white" />
          </div>
          <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
            {{ $t('serviceCard') }}
          </span>
        </RouterLink>
        <button
          class="w-fit ps-1 rounded-md bg-primary/80 hover:bg-primary flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-start"
          @click="onStartService(service)"
        >
          <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
            {{ $t('serviceStart') }}
          </span>
          <div class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-e">
            <FontAwesomeIcon
              :icon="faArrowCircleRight"
              class="w-4 fill-primary/80 hover:fill-primary text-white"
            />
          </div>
        </button>
      </template>
    </ServiceDirectory>
  </div>
</template>

<script setup>
import faGrip from '@/assets/icons/faGrip.svg?component'
import faGripLines from '@/assets/icons/faGripLines.svg?component'
import faListAlt from '@/assets/icons/faListAlt.svg?component'
import faStarOutline from '@/assets/icons/faStarOutline.svg?component'
import client_config from '@/assets/json/client_config.json'
import client_lookups from '@/assets/json/clientLookups.json'
import DashboardServiceDrillDown from '@/components/Dashboard/DashboardServiceDrillDown.vue'
import ServiceDirectory from '@/components/search/ServiceDirectory.vue'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'
import { useGlobalStore } from '@/stores/global'
import { faArrowCircleRight } from '@fortawesome/free-solid-svg-icons'
import { notify } from 'notiwind'
import { onMounted, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useFavoriteService } from '@/stores/favorite_services.js'
import FavoriteButton from '@/components/Buttons/Service/FavoriteButton.vue'

const router = useRouter()
const i18n = useI18n()
const authStore = useAuthStore()
const globalStore = useGlobalStore()
const dashboardStore = useDashboardStore()
const favoritesStore = useFavoriteService()

const isLoading = ref(true)
const selectedView = ref('list')

const sponsorDepartments = computed(() => {
  const departments = authStore.individualInfo.sponsorFileDepartments.map(
    (s) => s.immigrationDepartment.admninstrativeRegion
  )

  return departments.map((d) => {
    return {
      ...d,
      label: d.localizedName || d.text,
      value: d.id
    }
  })
})

onMounted(async () => {
  await authStore.fetchIndividualInfo()
  await dashboardStore.fetchAdminRegions()
  await dashboardStore.fetchServiceTransactions()
  isLoading.value = false
})

async function onStartService(service) {
  const { transactionId } = service
  const config = await globalStore.fetchConfig(transactionId)
  const { secondaryRequestUrl } = config

  const { v2ServiceFormTransactionIds } = client_config
  const {
    ServiceTransactions: { cancelLeavePermitForExemptionAndCreateNewOne },
    tahalufEstablishmentId
  } = client_lookups

  const {
    userClaimsObj: { EstablishmentId }
  } = authStore.user

  const isTahalufEst = EstablishmentId == tahalufEstablishmentId

  if (transactionId == cancelLeavePermitForExemptionAndCreateNewOne && isTahalufEst) {
    notify(
      {
        group: 'error',
        title: i18n.t('notAllowedService')
      },
      6000
    )
  }

  if (secondaryRequestUrl == v2ServiceFormTransactionIds.includes(transactionId)) {
    console.info('[onStartService] TODO: would you like to try our new form?')
    return
  }

  router.push({
    name: 'ServiceForm',
    params: { serviceId: transactionId, withException: false },
    query: {
      administrativeRegionId: dashboardStore.selectionFilter.region
    }
  })
}

onMounted(async () => {
  dashboardStore.selectionFilter.region = authStore.user.userAdminRegionId || 1

  await dashboardStore.fetchAdminRegions()
  await dashboardStore.fetchServiceTransactions()
  isLoading.value = false
})

async function onAddFavorite(service) {
  await favoritesStore.toggleFavoriteStatus(service)
  await favoritesStore.fetchFavorites()
}
</script>

<style lang="scss" scoped></style>
